// What a page's incoming router state says about its starting scope: which area type and country
// it was opened for, and (on a return trip from a preview) which delivery day and granularity.
// Pure: no React, router or Firebase imports. Router state is `unknown` here on purpose; anything
// that does not look right is treated as absent, never defaulted into a wrong country.
import { areaCountry, normalizeCountry } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { isActivityMeasure } from '../config/products';
import type { ActivityMeasure, Granularity, MeasureKey } from '../config/products';
import { isIsoDay } from './dateWindow';
import type { IsoDay } from './dateWindow';
import { asWindowUtc } from './submittedWindow';
import type { WindowUtc } from './submittedWindow';

export interface ActivityInitialScope {
  geoType: string | null;
  country: CountryCode | null;
  day: IsoDay | null;
  granularity: Granularity | null;
  /** The picker's query string (its country filter) the page was opened from, so Back returns to the same list. */
  pickerSearch: string | null;
  /** The Activities measure the job was run for, when it was handed back. */
  measure: ActivityMeasure | null;
  /** The instants the job was submitted with, when they were handed back. */
  windowUtc: WindowUtc | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const asString = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const asNonEmptyString = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
/** A query string such as '?country=EST'; anything else is absent. */
export const asSearch = (v: unknown): string | null => (typeof v === 'string' && v.startsWith('?') && v.length > 1 ? v : null);
const asGranularity = (v: unknown): Granularity | null => (v === 'hourly' || v === 'daily' ? v : null);
const asActivityMeasure = (v: unknown): ActivityMeasure | null =>
  typeof v === 'string' && isActivityMeasure(v as MeasureKey) ? (v as ActivityMeasure) : null;

/**
 * The starting scope for the Activities map page, from `location.state`.
 * - Returning from the preview (`preserveState`): the preserved area type, country, day, grain, measure
 *   and submitted window.
 *   This wins over `savedArea`, as it does when the page restores its job.
 * - Opened from a saved area: its area type (`geo_type`, else `type`) and its country (see
 *   areaCountry). A saved area whose country cannot be told, such as a custom polygon with none, gives null.
 * - Anything else: all null.
 */
export function activityInitialScope(state: unknown): ActivityInitialScope {
  const scope: ActivityInitialScope = { geoType: null, country: null, pickerSearch: null, day: null, granularity: null, measure: null, windowUtc: null };
  if (!isRecord(state)) return scope;

  if (state.preserveState) {
    scope.geoType = asNonEmptyString(state.preservedGeoType);
    scope.country = normalizeCountry(state.preservedCountryCode);
    scope.pickerSearch = asSearch(state.preservedPickerSearch);
    scope.day = isIsoDay(state.preservedDay) ? state.preservedDay : null;
    scope.granularity = asGranularity(state.preservedGranularity);
    scope.measure = asActivityMeasure(state.preservedMeasure);
    scope.windowUtc = asWindowUtc(state.preservedWindowUtc);
    return scope;
  }

  scope.pickerSearch = asSearch(state.pickerSearch);
  const area = state.savedArea;
  if (isRecord(area)) {
    scope.geoType = asNonEmptyString(area.geo_type) ?? asNonEmptyString(area.type);
    scope.country = areaCountry({ country_code: asString(area.country_code), type: asString(area.type) });
  }
  return scope;
}

/**
 * The area type an Origin-Destination selection stands for. The setup page calls Norwegian
 * municipalities 'municipalities'; the registry calls them 'admin_level_2'. No selection, or a
 * selection without a type, gives null (never a guessed grid).
 */
export function odmTarget(sel: { type?: unknown; [key: string]: unknown } | null | undefined): string | null {
  const type = sel?.type;
  if (type === 'municipalities') return 'admin_level_2';
  return asNonEmptyString(type);
}
